import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import {
    Box,
    Checkbox,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableContainer,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import { isPreviewAvailable, isThumbnailAvailable } from "../../schemas/documents";
import { documentsApi, useProductsDocuments } from "../../services/documentsService";
import { StyledTableCell, StyledTableHead } from "../table/BaseTable/BaseTable";

/**
 * Selector component for choosing documents related to specific products.
 *
 * TODO display document properties like name, industry, creation date
 * when it is available in the API response
 */
export const ProductDocumentsSelector = (props) => {
    const { selectedDocumentIds, onChange, productIds } = props;
    const { t } = useTranslation();
    const {
        data: allDocuments,
        isLoading,
        error,
    } = useProductsDocuments(productIds, {}, { include_cards_and_prospectus: true });

    const accessibleDocuments = useMemo(() => allDocuments || [], [allDocuments]);

    const selectedDocuments = useMemo(
        () => accessibleDocuments.filter((dp) => selectedDocumentIds.includes(dp.id)),
        [accessibleDocuments, selectedDocumentIds],
    );

    useEffect(()=>{
        if (!accessibleDocuments) {
            onChange([])
        }
        onChange(accessibleDocuments.filter((docs)=>docs.docs_content_type === 'application/pdf').map(docs=>docs.id))
    }, [accessibleDocuments])

    const toggleSelect = useCallback(
        (docId) => {
            if (selectedDocumentIds.includes(docId)) {
                onChange(selectedDocumentIds.filter((id) => id !== docId));
            } else {
                onChange([...selectedDocumentIds, docId]);
            }
        },
        [onChange, selectedDocumentIds],
    );

    if (isLoading) {
        return <div>{t("offers.SendOfferTab.documentsLoading")}</div>;
    }

    if (error) {
        return <div>{t("offers.SendOfferTab.documentsError")}</div>;
    }

    if (accessibleDocuments.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                    {t("offers.SendOfferTab.noAccessibleDocuments")}
                </Typography>
            </Box>
        );
    }

    return (
        <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={6}>
                <DocumentsTable
                    documents={accessibleDocuments}
                    showCheckboxColumn
                    selectedDocumentIds={selectedDocumentIds}
                    onToggleSelected={toggleSelect}
                />
            </Grid>
            <Grid item xs={6}>
                <DocumentsTable documents={selectedDocuments} showIdxColumn />
            </Grid>
        </Grid>
    );
};

const DocumentsTable = (props) => {
    const {
        documents,
        showIdxColumn = false,
        showCheckboxColumn = false,
        selectedDocumentIds,
        onToggleSelected,
    } = props;
    const { t } = useTranslation();
    const axiosPrivate = useAxiosPrivate();

    const [miniatureUrls, setMiniatureUrls] = useState({});

    // Fetch miniature URLs for documents that have thumbnails
    useEffect(() => {
        const fetchMiniatures = async () => {
            const urls = {};
            await Promise.all(
                documents.filter(isThumbnailAvailable).map(async (doc) => {
                    const { download_link } = await documentsApi.getDocumentDownloadLink(
                        axiosPrivate,
                        doc.id,
                    );
                    urls[doc.id] = await documentsApi.getBlobFromAzureByUrl(download_link);
                }),
            );
            setMiniatureUrls(urls);
        };

        fetchMiniatures();
    }, [documents, axiosPrivate]);

    const isSelected = useCallback(
        (docId) => selectedDocumentIds?.includes(docId) ?? false,
        [selectedDocumentIds],
    );

    if (showCheckboxColumn && (!onToggleSelected || !selectedDocumentIds)) {
        throw new Error(
            "onToggleSelected and selectedDocumentIds props are required when showCheckboxColumn is true",
        );
    }

    return (
        <TableContainer sx={{ width: "100%", maxWidth: "100%" }}>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                <StyledTableHead>
                    <TableRow>
                        {showCheckboxColumn && <StyledTableCell />}
                        {showIdxColumn && <StyledTableCell />}
                        <StyledTableCell>{t("offers.SendOfferTab.docs.name")}</StyledTableCell>
                        <StyledTableCell>{t("offers.SendOfferTab.docs.note")}</StyledTableCell>
                        <StyledTableCell>{t("offers.SendOfferTab.docs.preview")}</StyledTableCell>
                    </TableRow>
                </StyledTableHead>
                <TableBody>
                    {documents.map((dp, idx) => (
                        <TableRow key={dp.id}>
                            {showCheckboxColumn && (
                                <StyledTableCell>
                                    <Checkbox
                                        onChange={() => onToggleSelected(dp.id)}
                                        checked={isSelected(dp.id)}
                                    />
                                </StyledTableCell>
                            )}
                            {showIdxColumn && <StyledTableCell>{idx + 1}</StyledTableCell>}
                            <StyledTableCell>{dp.docs_name}</StyledTableCell>
                            <StyledTableCell>{dp.note}</StyledTableCell>
                            <StyledTableCell>
                                {isPreviewAvailable(dp) && (
                                    <DocumentPreviewButton
                                        document={dp}
                                        thumbnailUrl={miniatureUrls[dp.id]}
                                    />
                                )}
                            </StyledTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const DocumentPreviewButton = (props) => {
    const { document, thumbnailUrl } = props;
    const { t } = useTranslation();
    const axiosPrivate = useAxiosPrivate();

    const handlePreviewDoc = useCallback(async () => {
        try {
            await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, document.id);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_preview"));
        }
    }, [axiosPrivate, t, document.id]);

    return (
        <Tooltip title={t("other.file_table_cell.preview")}>
            <IconButton
                color="primary"
                onClick={handlePreviewDoc}
                sx={{
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {thumbnailUrl ? (
                    <Box
                        component="img"
                        src={thumbnailUrl}
                        alt=""
                        sx={{
                            width: "24px",
                            height: "24px",
                            objectFit: "contain",
                        }}
                    />
                ) : (
                    <PreviewOutlinedIcon fontSize="small" />
                )}
            </IconButton>
        </Tooltip>
    );
};
